const DB_NAME = 'ifa_investor_onboarding';
const DB_VERSION = 1;
const STORE_NAME = 'cmr_upload_files';
const PENDING_KEY = 'pending';

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function resolveStorageKey(sessionUuid) {
  const uuid = String(sessionUuid ?? '').trim();
  return uuid || PENDING_KEY;
}

function recordToFile(record) {
  if (!record?.blob) return null;
  const blob = record.blob;
  return new File([blob], record.name || 'cmr-document', {
    type: record.type || blob.type || 'application/octet-stream',
    lastModified: record.lastModified || Date.now(),
  });
}

export async function saveCmrFileToIndexedDB(file, sessionUuid) {
  if (!file) return;
  const key = resolveStorageKey(sessionUuid);
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const record = {
      blob: file,
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
      sessionUuid: key,
    };
    const req = store.put(record, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function readCmrFileFromIndexedDB(sessionUuid) {
  try {
    const key = resolveStorageKey(sessionUuid);
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => {
        resolve(recordToFile(req.result));
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

/** After session starts, move pending file blob to session uuid key. */
export async function promotePendingCmrFileToSession(sessionUuid) {
  if (!sessionUuid) return;
  const pendingFile = await readCmrFileFromIndexedDB(PENDING_KEY);
  if (!pendingFile) return;
  await saveCmrFileToIndexedDB(pendingFile, sessionUuid);
  await deleteCmrFileKey(PENDING_KEY);
}

async function deleteCmrFileKey(key) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    /* ignore */
  }
}

export async function clearCmrFileFromIndexedDB(sessionUuid) {
  const key = resolveStorageKey(sessionUuid);
  await deleteCmrFileKey(key);
}

export async function clearAllCmrFilesFromIndexedDB() {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    /* ignore */
  }
}
